import React, { useMemo, useState } from 'react';
import { Building2, KeyRound, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, EmptyState, ErrorState, IconButton, LoadingState } from '@/components/primitives';
import { Button } from '@/components/ui/button';
import { adminApi, formatDate, plural, toastError, useAdminQuery } from './adminApi';
import { AdminSection, Pager, RefreshError, SearchField, Toolbar } from './AdminUi';
import TypeToConfirmDialog from './TypeToConfirmDialog';
import OrganizationDialog from './OrganizationDialog';
import InviteLinkDialog from './InviteLinkDialog';

const PAGE = 25;

/** Tenants: one row per customer company, searchable, with a typed-name delete. */
export default function CompaniesTab({ onChanged }) {
  const { data, error, loading, reload, setData } = useAdminQuery(
    () => adminApi.get('/admin/companies/overview').then((r) => r.data.companies || []),
    []
  );
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [editor, setEditor] = useState(null); // { userId } or { userId: null } for a new organization
  const [invite, setInvite] = useState(null); // { invite, email, company }

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data || [];
    return (data || []).filter((c) =>
      [c.company_name, c.user_email, c.industry, c.region].some((v) => String(v || '').toLowerCase().includes(needle))
    );
  }, [data, q]);
  const page = rows.slice(offset, offset + PAGE);

  const issueLink = async (c) => {
    try {
      const res = await adminApi.post(`/admin/organizations/${c.user_id}/invite`);
      setInvite({ invite: res.data.invite, email: c.user_email, company: c.company_name });
    } catch (err) {
      toastError(err, 'Could not create the link');
    }
  };

  const saved = (result, wasEditing) => {
    reload();
    onChanged?.();
    if (!wasEditing && result.invite) {
      setInvite({ invite: result.invite, email: result.organization.user_email, company: result.organization.company_name });
    }
  };

  const deleteCompany = async () => {
    const c = deleting;
    try {
      await adminApi.delete(`/admin/companies/${c.user_id}`);
      setData((prev) => (prev || []).filter((x) => x.user_id !== c.user_id));
      toast.success(`${c.company_name} deleted`);
      onChanged?.();
    } catch (err) {
      toastError(err, `Could not delete ${c.company_name}`);
      throw err;
    }
  };

  return (
    <AdminSection
      title="Tenants"
      count={data ? data.length : null}
      description="Customer organizations. Accounts are created here only: there is no public sign-up."
      actions={<Button size="sm" onClick={() => setEditor({ userId: null })} data-testid="new-organization-btn"><Plus aria-hidden="true" /> New organization</Button>}
    >
      <Toolbar>
        <SearchField
          value={q}
          onChange={(v) => { setQ(v); setOffset(0); }}
          label="Search tenants"
          placeholder="Search company, email, industry or region"
        />
      </Toolbar>

      {loading ? (
        <LoadingState label="tenants" rows={5} />
      ) : error && !data ? (
        <ErrorState title="Couldn't load tenants" error={error} onRetry={reload} />
      ) : !data.length ? (
        <EmptyState icon={Building2} title="No organizations yet" description="Create the first customer organization to generate an invite link." action={<Button size="sm" onClick={() => setEditor({ userId: null })}><Plus aria-hidden="true" /> New organization</Button>} />
      ) : !rows.length ? (
        <EmptyState variant="compact" title="No matching tenants" description={`Nothing matched "${q}".`} />
      ) : (
        <>
          <RefreshError error={error} onRetry={reload} what="tenants" />
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--stack" data-testid="companies-table">
              <thead>
                <tr>
                  <th scope="col">Company</th>
                  <th scope="col">Primary contact</th>
                  <th scope="col">Industry</th>
                  <th scope="col">Profile</th>
                  <th scope="col" className="adm-num">Matched threats</th>
                  <th scope="col" className="adm-num">Domains</th>
                  <th scope="col" className="adm-num">Breach results</th>
                  <th scope="col">Created</th>
                  <th scope="col"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {page.map((c) => (
                  <tr key={c.user_id || c.id} data-testid={`company-row-${c.id}`}>
                    <td data-label="Company">
                      <div>
                        <div className="adm-strong">
                          {c.company_name || 'Unnamed company'}{' '}
                          {c.status === 'invited' ? <Badge size="sm" tone="warning" plain>Invited</Badge> : <Badge size="sm" tone="success" plain>Active</Badge>}
                        </div>
                        {(c.company_size || c.num_employees) ? (
                          <div className="adm-meta">
                            {[c.company_size, c.num_employees ? plural(Number(c.num_employees), 'employee') : null].filter(Boolean).join(' · ')}
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td data-label="Primary contact" className="adm-mono adm-break">
                      {c.user_email || <span className="adm-muted">No user account</span>}
                    </td>
                    <td data-label="Industry">{c.industry || '-'}</td>
                    <td data-label="Profile" className="adm-nowrap">
                      <span>
                        <span className="adm-mono" title="How many of the detailed insight fields are filled in">{c.completeness?.percent ?? 0}%</span>
                        <span className="adm-meta"> · {c.region || 'no region'}</span>
                      </span>
                    </td>
                    <td data-label="Matched threats" className="adm-num">{c.matched_threats}</td>
                    <td data-label="Domains" className="adm-num">{c.monitored_domains}</td>
                    <td data-label="Breach results" className="adm-num">{c.breach_results}</td>
                    <td data-label="Created" className="adm-mono adm-nowrap">{formatDate(c.created_at)}</td>
                    <td className="adm-actions-cell">
                      <IconButton label={`Edit ${c.company_name || 'organization'}`} icon={Pencil} variant="outline" onClick={() => setEditor({ userId: c.user_id })} data-testid={`edit-company-${c.user_id}`} />
                      <IconButton label={c.status === 'invited' ? `New invite link for ${c.company_name}` : `Password reset link for ${c.company_name}`} icon={KeyRound} variant="outline" onClick={() => issueLink(c)} data-testid={`link-company-${c.user_id}`} />
                      <IconButton
                        label={`Delete ${c.company_name || 'company'}`}
                        icon={Trash2}
                        variant="danger"
                        onClick={() => setDeleting(c)}
                        data-testid={`delete-company-${c.user_id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} limit={PAGE} total={rows.length} onChange={setOffset} label="Tenant pages" />
        </>
      )}

      <OrganizationDialog
        open={Boolean(editor)}
        userId={editor?.userId || null}
        onOpenChange={(o) => !o && setEditor(null)}
        onSaved={saved}
      />
      <InviteLinkDialog invite={invite?.invite} email={invite?.email} company={invite?.company} onClose={() => setInvite(null)} />

      <TypeToConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={`Delete ${deleting?.company_name || 'this company'}?`}
        description="This permanently removes the tenant and cannot be undone."
        expected={deleting?.company_name || ''}
        confirmLabel="Delete tenant"
        onConfirm={deleteCompany}
      >
        {deleting && (
          <ul className="adm-list-bullets">
            <li>The user account {deleting.user_email ? <span className="adm-mono">{deleting.user_email}</span> : null} and company profile</li>
            <li>{plural(deleting.matched_threats, 'matched threat')}, notifications and inbox triage state</li>
            <li>{plural(deleting.monitored_domains, 'monitored domain')}, {plural(deleting.monitored_emails, 'monitored email')} and {plural(deleting.breach_results, 'breach result')}</li>
            <li>Webhooks, roles and preferences</li>
          </ul>
        )}
      </TypeToConfirmDialog>
    </AdminSection>
  );
}
